/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, type PropsWithChildren } from 'react';
import { cssMap, jsx } from '@compiled/react';
import Heading from '@atlaskit/heading/heading';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import TooltipPrimitive, { type TooltipPrimitiveProps } from '@atlaskit/tooltip/TooltipPrimitive';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

const title = defineMessage({
	id: 'polaris-component-limits.title',
	defaultMessage: 'Unavailable',
	description: 'Title for the entity limit popup',
});

type CreateViewTooltipProps = {
	message: React.ReactNode;
};

const styles = cssMap({
	textContainer: {
		backgroundColor: token('elevation.surface'),
		paddingTop: token('space.200'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.300'),
		font: token('font.body'),
		color: token('color.text'),
	},

	tooltip: {
		padding: 0,
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
		width: '320px',
	},
});

export const CreateEntityLimitTooltipContent = ({
	message,
}: CreateViewTooltipProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.textContainer}>
			<Stack space="space.100">
				<Inline space="space.050" alignBlock="center">
					<WarningIcon spacing="spacious" label={formatMessage(title)} />
					<Heading size="xsmall">{formatMessage(title)}</Heading>
				</Inline>
				<Inline space="space.100">{message}</Inline>
			</Stack>
		</Box>
	);
};

export const CreateEntityLimitTooltip = ({
	children,
	message,
}: PropsWithChildren<CreateViewTooltipProps>): React.JSX.Element => {
	if (!message) {
		return <>{children}</>;
	}

	return (
		<Tooltip
			position="right"
			content={<CreateEntityLimitTooltipContent message={message} />}
			component={TooltipComponent}
		>
			{children}
		</Tooltip>
	);
};

const TooltipComponent: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<TooltipPrimitiveProps> & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(({ children, className, ...rest }, ref) => {
	// https://atlassian.design/components/tooltip/examples#customizing-tooltip
	return (
		<TooltipPrimitive
			{...rest}
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/no-classname-prop
			className={className}
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
			css={styles.tooltip}
			ref={ref}
		>
			{children}
		</TooltipPrimitive>
	);
});
